import { ReactElement, useCallback } from 'react';
import { formatTranslation } from 'intl';
import { DocumentationTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Undo as UndoIcon } from '@mui/icons-material';
import { SearchInput } from 'components/common/SearchInput';
import { useDocumentationStore } from 'store/documentation';
import useIsMobile from 'hooks/useIsMobile';

export function DocumentationSearchInput(): ReactElement {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const searchPhrase = useDocumentationStore.use.searchPhrase();
  const setSearchPhrase = useDocumentationStore.use.setSearchPhrase();
  const savedSearchPhrase = useDocumentationStore.use.savedSearchPhrase();
  const returnToSearchResults = useDocumentationStore.use.returnToSearchResults();

  const handleSearchChange = useCallback(
    (value: string): void => {
      setSearchPhrase(value);
    },
    [setSearchPhrase]
  );

  const handleSearchClear = useCallback((): void => {
    setSearchPhrase('');
  }, [setSearchPhrase]);

  const tooltipTitle = savedSearchPhrase
    ? formatTranslation(intl, DocumentationTranslationKeys.returnToSearchTooltip, { phrase: savedSearchPhrase })
    : '';

  const searchInputNode = (
    <Box
      sx={{
        ml: isMobile ? 0 : 2,
        mr: 0,
        display: 'flex',
        alignItems: 'center',
        flex: isMobile ? 1 : '0 1 auto',
        minWidth: 0,
      }}
    >
      <SearchInput
        value={searchPhrase}
        searchChange={handleSearchChange}
        searchClear={handleSearchClear}
        dataQa='toolbar-search-input'
        placeholderTextId='dashboard_search_placeholder'
      />
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        flex: isMobile ? 1 : '0 1 auto',
      }}
    >
      {searchInputNode}
      {savedSearchPhrase && (
        <Tooltip title={tooltipTitle} arrow>
          <IconButton
            onClick={returnToSearchResults}
            size="small"
            sx={{
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.08)',
              },
            }}
          >
            <UndoIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
